/**
 * Analytics utility for Google Analytics 4 event tracking
 * Tracks conversions and user behaviors across the Tally app
 */

// Check if gtag is available
const isGtagAvailable = () => typeof window !== 'undefined' && typeof window.gtag === 'function';

/**
 * Track a custom event in Google Analytics
 * @param {string} eventName - The name of the event
 * @param {object} params - Additional parameters for the event
 */
export const trackEvent = (eventName, params = {}) => {
  if (!isGtagAvailable()) {
    console.debug('[Analytics] gtag not available');
    return;
  }
  
  window.gtag('event', eventName, {
    ...params,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track page views (called by React Router)
 * @param {string} pagePath - The path of the page
 * @param {string} pageTitle - The title of the page
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (!isGtagAvailable()) return;
  
  window.gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle,
    page_location: window.location.href
  });
};

// ==================== CONVERSION EVENTS ====================

/**
 * Track user signup completion
 * @param {string} method - The signup method (email, google, etc.)
 * @param {string} plan - The selected plan (student, power, senior)
 */
export const trackSignup = (method = 'email', plan = 'power') => {
  trackEvent('sign_up', {
    method,
    plan,
    event_category: 'conversion',
    event_label: `signup_${plan}`
  });
  
  // Also track as a conversion
  trackEvent('conversion', {
    send_to: 'G-PGDML4BMXD',
    event_category: 'signup',
    value: plan === 'power' ? 14.99 : 0,
    currency: 'CAD'
  });

  // Reddit Pixel — SignUp event
  if (window.rdt) {
    window.rdt('track', 'SignUp');
  }
};

/**
 * Track user login
 * @param {string} method - The login method
 */
export const trackLogin = (method = 'email') => {
  trackEvent('login', {
    method,
    event_category: 'engagement'
  });
};

/**
 * Track bank connection via Plaid
 * @param {string} institution - The bank/institution name
 * @param {number} accountsConnected - Number of accounts connected
 */
export const trackBankConnection = (institution = 'unknown', accountsConnected = 1) => {
  trackEvent('bank_connected', {
    institution,
    accounts_connected: accountsConnected,
    event_category: 'conversion',
    event_label: 'plaid_connection'
  });
};

/**
 * Track subscription purchase/trial start
 * @param {string} plan - The subscription plan
 * @param {number} value - The monetary value
 * @param {boolean} isTrial - Whether it's a trial
 */
export const trackSubscription = (plan, value, isTrial = true) => {
  trackEvent('purchase', {
    currency: 'CAD',
    value,
    items: [{
      item_id: `tally_${plan}`,
      item_name: `Tally ${plan.charAt(0).toUpperCase() + plan.slice(1)}`,
      price: value
    }],
    event_category: 'conversion',
    event_label: isTrial ? 'trial_start' : 'subscription_purchase'
  });
  
  trackEvent('begin_checkout', {
    currency: 'CAD',
    value,
    event_category: 'ecommerce'
  });

  // Reddit Pixel — Purchase event
  if (window.rdt) {
    window.rdt('track', 'Purchase', { value: value, currency: 'CAD' });
  }
};

/**
 * Track trial to paid conversion
 * @param {string} plan - The subscription plan
 * @param {number} value - The monetary value
 */
export const trackTrialConversion = (plan, value) => {
  trackEvent('trial_converted', {
    currency: 'CAD',
    value,
    plan,
    event_category: 'conversion',
    event_label: 'trial_to_paid'
  });

  // Reddit Pixel — Purchase event for trial-to-paid conversion
  if (window.rdt) {
    window.rdt('track', 'Purchase', { value: value, currency: 'CAD' });
  }
};

// ==================== ENGAGEMENT EVENTS ====================

/**
 * Track bill marked as paid
 * @param {string} billId - The bill ID
 * @param {number} amount - The bill amount
 */
export const trackBillPaid = (billId, amount) => {
  trackEvent('bill_paid', {
    bill_id: billId,
    value: amount,
    currency: 'CAD',
    event_category: 'engagement',
    event_label: 'bill_payment'
  });
};

/**
 * Track transaction categorization
 * @param {string} fromCategory - Original category
 * @param {string} toCategory - New category
 */
export const trackCategoryChange = (fromCategory, toCategory) => {
  trackEvent('category_changed', {
    from_category: fromCategory,
    to_category: toCategory,
    event_category: 'engagement'
  });
};

/**
 * Track budget created/updated
 * @param {string} category - The budget category
 * @param {number} amount - The budget amount
 */
export const trackBudgetUpdate = (category, amount) => {
  trackEvent('budget_updated', {
    category,
    budget_amount: amount,
    currency: 'CAD',
    event_category: 'engagement'
  });
};

/**
 * Track financial goal created
 * @param {string} goalType - The type of goal
 * @param {number} targetAmount - The goal target amount
 */
export const trackGoalCreated = (goalType, targetAmount) => {
  trackEvent('goal_created', {
    goal_type: goalType,
    target_amount: targetAmount,
    currency: 'CAD',
    event_category: 'engagement',
    event_label: 'financial_goal'
  });
};

/**
 * Track household created
 * @param {string} householdName - The household name
 */
export const trackHouseholdCreated = (householdName) => {
  trackEvent('household_created', {
    household_name: householdName,
    event_category: 'engagement',
    event_label: 'multi_user'
  });
};

/**
 * Track household member joined
 */
export const trackHouseholdJoined = () => {
  trackEvent('household_joined', {
    event_category: 'engagement',
    event_label: 'multi_user'
  });
};

/**
 * Track calendar sync enabled
 * @param {string} calendarType - The calendar type (google, outlook, ics)
 */
export const trackCalendarSync = (calendarType) => {
  trackEvent('calendar_synced', {
    calendar_type: calendarType,
    event_category: 'engagement',
    event_label: 'calendar_integration'
  });
};

/**
 * Track course started
 * @param {string} courseId - The course ID
 * @param {string} courseName - The course name
 */
export const trackCourseStarted = (courseId, courseName) => {
  trackEvent('course_started', {
    course_id: courseId,
    course_name: courseName,
    event_category: 'engagement',
    event_label: 'education'
  });
};

/**
 * Track course completed
 * @param {string} courseId - The course ID
 * @param {string} courseName - The course name
 */
export const trackCourseCompleted = (courseId, courseName) => {
  trackEvent('course_completed', {
    course_id: courseId,
    course_name: courseName,
    event_category: 'conversion',
    event_label: 'education_completed'
  });
};

/**
 * Track AI summary viewed
 * @param {string} page - The page where summary was viewed
 */
export const trackAISummaryViewed = (page) => {
  trackEvent('ai_summary_viewed', {
    page,
    event_category: 'engagement',
    event_label: 'ai_feature'
  });
};

// ==================== USER PROPERTIES ====================

/**
 * Set user properties for segmentation
 * @param {string} userId - The user ID
 * @param {object} properties - User properties
 */
export const setUserProperties = (userId, properties = {}) => {
  if (!isGtagAvailable()) return;
  
  window.gtag('set', 'user_properties', {
    user_id: userId,
    ...properties
  });
  
  // Also set user ID for cross-device tracking
  window.gtag('config', 'G-PGDML4BMXD', {
    user_id: userId
  });
};

/**
 * Set user plan/tier
 * @param {string} plan - The user's plan (student, power, senior)
 */
export const setUserPlan = (plan) => {
  if (!isGtagAvailable()) return;
  
  window.gtag('set', 'user_properties', {
    plan_type: plan,
    is_premium: plan === 'power'
  });
};

// ==================== ECOMMERCE EVENTS ====================

/**
 * Track pricing page view
 * @param {string} source - Where user came from
 */
export const trackPricingView = (source = 'direct') => {
  trackEvent('view_pricing', {
    source,
    event_category: 'funnel',
    event_label: 'pricing_page'
  });
  
  // Track as view_item_list for ecommerce
  trackEvent('view_item_list', {
    item_list_id: 'pricing_plans',
    item_list_name: 'Pricing Plans',
    items: [
      { item_id: 'tally_student', item_name: 'Tally Student', price: 0 },
      { item_id: 'tally_power', item_name: 'Tally Power', price: 14.99 },
      { item_id: 'tally_senior', item_name: 'Tally Senior', price: 0 }
    ]
  });
};

/**
 * Track plan selection
 * @param {string} plan - The selected plan
 * @param {number} price - The plan price
 */
export const trackPlanSelected = (plan, price) => {
  trackEvent('select_item', {
    item_list_id: 'pricing_plans',
    items: [{
      item_id: `tally_${plan}`,
      item_name: `Tally ${plan.charAt(0).toUpperCase() + plan.slice(1)}`,
      price
    }],
    event_category: 'funnel',
    event_label: 'plan_selected'
  });
};

// ==================== ERROR TRACKING ====================

/**
 * Track errors for debugging
 * @param {string} errorType - Type of error
 * @param {string} errorMessage - Error message
 * @param {string} page - Page where error occurred
 */
export const trackError = (errorType, errorMessage, page) => {
  trackEvent('error', {
    error_type: errorType,
    error_message: errorMessage,
    page,
    event_category: 'error'
  });
};

// ==================== DEMO FUNNEL EVENTS ====================

export const trackDemoStarted = () => {
  trackEvent('demo_started', { event_category: 'conversion' });
  if (window.rdt) window.rdt('track', 'ViewContent');
};

export const trackDemoStep = (step) => {
  trackEvent('demo_step', { step, event_category: 'engagement' });
};

export const trackDemoCompleted = () => {
  trackEvent('demo_completed', { event_category: 'conversion' });
  if (window.rdt) window.rdt('track', 'Lead');
};

export default {
  trackEvent,
  trackPageView,
  trackSignup,
  trackLogin,
  trackBankConnection,
  trackSubscription,
  trackTrialConversion,
  trackBillPaid,
  trackCategoryChange,
  trackBudgetUpdate,
  trackGoalCreated,
  trackHouseholdCreated,
  trackHouseholdJoined,
  trackCalendarSync,
  trackCourseStarted,
  trackCourseCompleted,
  trackAISummaryViewed,
  setUserProperties,
  setUserPlan,
  trackPricingView,
  trackPlanSelected,
  trackError
};
